import axios from 'axios'
import {
  AnalysisResult,
  AnalysisRequest,
  HistoryItem,
  SystemStats,
  User,
  LoginRequest,
  CreateUserRequest,
  UpdateUserRequest,
  Session,
  SessionCreate,
  SessionUpdate,
  TestingSessionCreate,
  Token,
  Room,
  RoomCreate,
  SessionAnalysesResponse,
  RoomSection,
  RoomSectionCreate,
  RoomSectionUpdate,
  SessionSectionCreate,
  SectionValidationResult,
  Category,
  ClassThumbnail,
  ClassItem,
} from '@/types'

const api = axios.create({
  baseURL: '/api',
  timeout: 180000, // 3 minutes for image processing
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Don't log requests for health check endpoints
    if (!config.url?.includes('/health')) {
      console.log(`Making ${config.method?.toUpperCase()} request to ${config.url}`)
    }
    
    // Add auth token to requests
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    return config
  },
  (error) => {
    console.error('Request error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    // Don't log errors for health check endpoints
    if (!error.config?.url?.includes('/health')) {
      console.error('API Error:', error.response?.data || error.message)
    }
    
    // Handle 401 unauthorized errors
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user_data')
      // Trigger logout by dispatching a custom event
      window.dispatchEvent(new CustomEvent('auth:logout'))
    }
    
    return Promise.reject(error)
  }
)

export const apiService = {
  // Health check with timeout and error handling
  healthCheck: async () => {
    try {
      const response = await api.get('/health', {
        timeout: 3000, // 3 second timeout
      })
      return response.data
    } catch (error: any) {
      // Normalize different types of connection errors
      if (error.code === 'ECONNREFUSED' || 
          error.code === 'ENOTFOUND' ||
          error.code === 'ECONNRESET' ||
          error.name === 'AxiosError' ||
          error.message?.includes('Network Error') ||
          error.message?.includes('connect ECONNREFUSED')) {
        throw new Error('Backend unavailable')
      }
      throw error
    }
  },

  // Analyze image
  analyzeImage: async (
    file: File,
    options: AnalysisRequest
  ): Promise<AnalysisResult> => {
    const formData = new FormData()
    formData.append('file', file)

    // Add query parameters for analysis options
    const params = new URLSearchParams()
    params.append('session_id', options.session_id.toString())
    if (options.section_id) params.append('section_id', options.section_id.toString())
    if (options.confidence_threshold) params.append('confidence_threshold', options.confidence_threshold.toString())
    if (options.k) params.append('k', options.k.toString())

    const response = await api.post(`/analyze?${params}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    
    return response.data
  },

  // Get analysis history
  getHistory: async (limit = 10, offset = 0) => {
    const response = await api.get(`/history?limit=${limit}&offset=${offset}`)
    return response.data
  },

  // Get system statistics
  getStats: async (): Promise<SystemStats> => {
    const response = await api.get('/stats')
    return response.data
  },

  // Delete analysis
  deleteAnalysis: async (analysisId: number) => {
    const response = await api.delete(`/history/${analysisId}`)
    return response.data
  },

  // Authentication
  login: async (credentials: LoginRequest): Promise<Token> => {
    const response = await api.post('/auth/login', credentials)
    return response.data
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await api.get('/auth/me')
    return response.data
  },

  // Admin user management
  getAllUsers: async (limit = 50, offset = 0) => {
    const response = await api.get(`/admin/users?limit=${limit}&offset=${offset}`)
    return response.data
  },

  createUser: async (userData: CreateUserRequest): Promise<Token> => {
    const response = await api.post('/admin/users', userData)
    return response.data
  },

  updateUser: async (userId: number, userData: UpdateUserRequest) => {
    const response = await api.put(`/admin/users/${userId}`, userData)
    return response.data
  },

  deleteUser: async (userId: number) => {
    const response = await api.delete(`/admin/users/${userId}`)
    return response.data
  },

  // Room management
  getRooms: async (): Promise<{ rooms: Room[] }> => {
    const response = await api.get('/rooms') // Changed to /api/rooms
    return response.data
  },

  getRoom: async (roomId: number): Promise<Room> => {
    const response = await api.get(`/rooms/${roomId}`)
    return response.data
  },

  createRoom: async (roomData: RoomCreate): Promise<Room> => {
    const response = await api.post('/admin/rooms', roomData)
    return response.data
  },

  updateRoom: async (roomId: number, roomData: RoomCreate): Promise<Room> => {
    const response = await api.put(`/admin/rooms/${roomId}`, roomData)
    return response.data
  },

  deleteRoom: async (roomId: number): Promise<void> => {
    await api.delete(`/admin/rooms/${roomId}`)
  },

  // Session management
  getSessions: async (limit = 50, offset = 0) => {
    const response = await api.get(`/sessions?limit=${limit}&offset=${offset}`)
    return response.data
  },

  getSessionSummaries: async (sessionIds: number[]): Promise<{
    summaries: Record<string, {
      session_id: number;
      sections: Array<{
        section_id: number | null;
        section_name: string;
        image_count: number;
        detection_count: number;
        status: 'no_upload' | 'not_validated' | 'validated';
      }>;
      total_detections: number;
      overall_status: 'not_started' | 'incomplete_sections' | 'awaiting_validation' | 'complete';
    }>;
  }> => {
    if (sessionIds.length === 0) return { summaries: {} }
    const response = await api.get(`/sessions/summaries?session_ids=${sessionIds.join(',')}`)
    return response.data
  },

  createSession: async (sessionData: SessionCreate): Promise<Session> => {
    const response = await api.post('/sessions', sessionData)
    return response.data
  },

  getSession: async (sessionId: number): Promise<Session> => {
    const response = await api.get(`/sessions/${sessionId}`)
    return response.data
  },

  updateSession: async (sessionId: number, sessionData: SessionUpdate) => {
    const response = await api.put(`/sessions/${sessionId}`, sessionData)
    return response.data
  },

  updateSessionStatus: async (sessionId: number, statusData: { is_active: boolean, status: string }) => {
    const response = await api.put(`/sessions/${sessionId}`, statusData)
    return response.data
  },

  deleteSession: async (sessionId: number) => {
    const response = await api.delete(`/sessions/${sessionId}`)
    return response.data
  },

  getSessionAnalyses: async (sessionId: number, limit = 50, offset = 0): Promise<SessionAnalysesResponse> => {
    const response = await api.get(`/sessions/${sessionId}/analyses?limit=${limit}&offset=${offset}`)
    return response.data
  },

  // Get analysis image as blob with authentication
  getAnalysisImageBlob: async (analysisId: number): Promise<Blob> => {
    const response = await api.get(`/images/${analysisId}`, {
      responseType: 'blob',
      timeout: 10000 // 10 seconds for image loading
    })
    return response.data
  },

  // Get analysis image URL for ImageViewer (fallback method)
  getAnalysisImageUrl: (analysisId: number) => {
    const token = localStorage.getItem('auth_token')
    return `${api.defaults.baseURL}/images/${analysisId}?token=${token}&t=${Date.now()}`
  },

  // User corrections API
  saveUserCorrection: async (sessionId: number, correction: {
    image_key: string
    detection_id: number
    original_class: string
    corrected_class: string
    analysis_id?: number
  }) => {
    const response = await api.post(`/sessions/${sessionId}/corrections`, correction)
    return response.data
  },

  getUserCorrections: async (sessionId: number) => {
    const response = await api.get(`/sessions/${sessionId}/corrections`)
    return response.data
  },

  deleteUserCorrection: async (sessionId: number, imageKey: string, detectionId: number) => {
    const response = await api.delete(`/sessions/${sessionId}/corrections/${encodeURIComponent(imageKey)}/${detectionId}`)
    return response.data
  },

  // Session settings API
  saveSessionSettings: async (sessionId: number, settings: {
    conf_threshold: number
    iou_threshold: number
    yolo_img_size: number
    cutoff_threshold: number
    top_distances: number
    major_votes: number
    show_uncertain_detections?: boolean
    refiner_enabled?: boolean
    refiner_threshold?: number
    refiner_trigger_threshold?: number
    disable_manual_detection_discard?: boolean
  }) => {
    const response = await api.post(`/sessions/${sessionId}/settings`, settings)
    return response.data
  },

  getSessionSettings: async (sessionId: number) => {
    const response = await api.get(`/sessions/${sessionId}/settings`)
    return response.data
  },

  updateSessionSettings: async (sessionId: number, settings: {
    conf_threshold?: number
    iou_threshold?: number
    yolo_img_size?: number
    cutoff_threshold?: number
    top_distances?: number
    major_votes?: number
  }) => {
    const response = await api.put(`/sessions/${sessionId}/settings`, settings)
    return response.data
  },

  deleteSessionSettings: async (sessionId: number) => {
    const response = await api.delete(`/sessions/${sessionId}/settings`)
    return response.data
  },

  // Get default analysis settings from backend configuration
  getDefaultSettings: async () => {
    const response = await api.get('/default-settings')
    return response.data
  },

  // Global settings management (admin only)
  getGlobalSettings: async () => {
    const response = await api.get('/admin/settings')
    return response.data
  },

  updateGlobalSettings: async (settings: {
    conf_threshold?: number
    iou_threshold?: number
    yolo_img_size?: number
    cutoff_threshold?: number
    top_distances?: number
    major_votes?: number
    show_uncertain_detections?: boolean
    refiner_enabled?: boolean
    refiner_threshold?: number
    refiner_trigger_threshold?: number
    disable_manual_detection_discard?: boolean
  }) => {
    const response = await api.put('/admin/settings', settings)
    return response.data
  },

  syncGlobalSettingsFromEnv: async () => {
    const response = await api.post('/admin/settings/sync-from-env')
    return response.data
  },

  // Change user password
  changePassword: async (passwordData: {
    current_password: string
    new_password: string
  }) => {
    const response = await api.put('/auth/change-password', passwordData)
    return response.data
  },

  // User-drawn detections API
  createUserDetection: async (sessionId: number, detection: {
    image_key: string
    bbox: [number, number, number, number]
    display_coords: [number, number, number, number]
    image_dimensions: { naturalWidth: number, naturalHeight: number, clientWidth: number, clientHeight: number }
  }) => {
    const formData = new FormData()
    formData.append('image_key', detection.image_key)
    formData.append('bbox', JSON.stringify(detection.bbox))
    formData.append('display_coords', JSON.stringify(detection.display_coords))
    formData.append('image_dimensions', JSON.stringify(detection.image_dimensions))

    const response = await api.post(`/sessions/${sessionId}/user-detections`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  // Delete detection API
  deleteDetection: async (sessionId: number, analysisId: number, detectionId: number) => {
    const response = await api.delete(`/sessions/${sessionId}/analyses/${analysisId}/detections/${detectionId}`)
    return response.data
  },

  // Enrollment/Catalog API
  getEnrollmentClasses: async () => {
    const response = await api.get('/enrollment/classes');
    return response.data;
  },

  getClassImages: async (classId: string) => {
    const response = await api.get(`/enrollment/classes/${classId}/images`)
    return response.data
  },

  createClass: async (classData: {
    folderName: string
    displayName: string
  }) => {
    const response = await api.post('/enrollment/classes', classData)
    return response.data
  },

  updateClass: async (classId: string, updates: {
    folderName?: string
    displayName?: string
  }) => {
    const response = await api.put(`/enrollment/classes/${classId}`, updates)
    return response.data
  },

  deleteClass: async (classId: string) => {
    const response = await api.delete(`/enrollment/classes/${encodeURIComponent(classId)}`)
    return response.data
  },

  uploadImages: async (classId: string, formData: FormData) => {
    const response = await api.post(`/enrollment/classes/${classId}/images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  importZipClass: async (file: File, className?: string): Promise<{
    task_id: string
    class_name: string
    message: string
    total_files: number
  }> => {
    const formData = new FormData()
    formData.append('file', file)
    if (className) {
      formData.append('class_name', className)
    }

    const response = await api.post('/enrollment/import-zip', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  deleteImage: async (imageId: string) => {
    // Parse the image ID to extract class_id and filename
    // Format: {class_id}_{filename}
    const underscoreIndex = imageId.indexOf('_')
    if (underscoreIndex === -1) {
      throw new Error('Invalid image ID format')
    }

    const class_id = imageId.substring(0, underscoreIndex)
    const filename = imageId.substring(underscoreIndex + 1)

    console.log(`Deleting image - class_id: ${class_id}, filename: ${filename}`)

    // Use the new parameter-based endpoint
    const response = await api.delete('/enrollment/delete-image', {
      params: {
        class_id: class_id,
        filename: filename
      }
    })
    return response.data
  },

  // Category management
  getAllCategories: async (): Promise<Record<string, Category & { classCount: number; classes: string[] }>> => {
    const response = await api.get('/enrollment/categories')
    return response.data
  },

  // Category registry CRUD (admin)
  createCategory: async (category: Category): Promise<void> => {
    await api.post('/enrollment/categories', category)
  },

  updateCategory: async (categoryName: string, updates: Category): Promise<void> => {
    await api.put(`/enrollment/categories/${encodeURIComponent(categoryName)}`, updates)
  },

  deleteCategory: async (categoryName: string): Promise<void> => {
    await api.delete(`/enrollment/categories/${encodeURIComponent(categoryName)}`)
  },

  getClassCategory: async (classId: string): Promise<Category | null> => {
    const response = await api.get(`/enrollment/classes/${classId}/category`)
    return response.data
  },

  setClassCategory: async (classId: string, category: Category): Promise<void> => {
    await api.put(`/enrollment/classes/${classId}/category`, category)
  },

  removeClassCategory: async (classId: string): Promise<void> => {
    await api.delete(`/enrollment/classes/${classId}/category`)
  },

  // Thumbnail management
  getClassThumbnail: async (classId: string): Promise<ClassThumbnail | null> => {
    const response = await api.get(`/enrollment/classes/${classId}/thumbnail`)
    return response.data
  },

  setClassThumbnail: async (classId: string, filename: string): Promise<ClassThumbnail> => {
    const response = await api.put(`/enrollment/classes/${classId}/thumbnail`, { filename })
    return response.data.thumbnail
  },

  autoSelectThumbnail: async (classId: string): Promise<ClassThumbnail> => {
    const response = await api.post(`/enrollment/classes/${classId}/thumbnail/auto`)
    return response.data.thumbnail
  },

  getThumbnailUrl: (classId: string, thumbnailPath: string): string => {
    return `/api/enrollment/serve-image?class_id=${classId}&filename=${thumbnailPath}`
  },

  // Embedding index management
  getIndexStatus: async () => {
    const response = await api.get('/enrollment/index/status')
    return response.data
  },

  initializeEmbeddingIndex: async () => {
    const response = await api.post('/enrollment/index/initialize')
    return response.data
  },

  rebuildEmbeddingIndex: async () => {
    const response = await api.post('/enrollment/index/rebuild')
    return response.data
  },

  updateEmbeddingIndex: async (operation: string, data: any) => {
    const response = await api.post('/enrollment/index/update', {
      operation,
      data
    })
    return response.data
  },

  // Background task APIs
  getAllTasks: async () => {
    const response = await api.get('/enrollment/tasks')
    return response.data
  },

  getActiveTasks: async () => {
    const response = await api.get('/enrollment/tasks/active')
    return response.data
  },

  getTaskStatus: async (taskId: string) => {
    const response = await api.get(`/enrollment/tasks/${taskId}`)
    return response.data
  },

  getEmbeddingParquetData: async () => {
    const response = await api.get('/enrollment/embeddings/parquet')
    return response.data
  },

  getEmbeddingVector: async (itemId: string) => {
    const response = await api.get(`/enrollment/embeddings/vector/${itemId}`)
    return response.data
  },

  // Authenticated image fetching
  // size options: 'thumb' (150px), 'small' (300px), 'medium' (600px), 'large' (1200px), 'full' (original)
  getEnrollmentImage: async (classId: string, filename: string, size?: string): Promise<Blob> => {
    const response = await api.get('/enrollment/serve-image', {
      params: {
        class_id: classId,
        filename: filename,
        ...(size && size !== 'full' ? { size } : {})
      },
      responseType: 'blob'
    })
    return response.data
  },

  // Tag management API
  getAllTags: async () => {
    const response = await api.get('/tags')
    return response.data
  },

  getAllClassTags: async () => {
    const response = await api.get('/tags/all-classes')
    return response.data
  },

  getClassTag: async (className: string) => {
    const response = await api.get(`/tags/class/${encodeURIComponent(className)}`)
    return response.data
  },

  assignTag: async (className: string, tag: string) => {
    const response = await api.post('/tags', {
      class_name: className,
      tag: tag
    })
    return response.data
  },

  removeTag: async (className: string) => {
    const response = await api.delete(`/tags/class/${encodeURIComponent(className)}`)
    return response.data
  },

  // Bulk class operations
  bulkAssignCategory: async (classIds: string[], category: { name: string; color: string }) => {
    const response = await api.post('/enrollment/classes/bulk/category', {
      class_ids: classIds,
      category
    })
    return response.data
  },

  bulkAssignTag: async (classIds: string[], tag: string) => {
    const response = await api.post('/enrollment/classes/bulk/tag', {
      class_ids: classIds,
      tag
    })
    return response.data
  },

  bulkResetClasses: async (classIds: string[]) => {
    const response = await api.post('/enrollment/classes/bulk/reset', {
      class_ids: classIds
    })
    return response.data
  },

  // Section management API
  getSection: async (sectionId: number): Promise<RoomSection> => {
    const response = await api.get(`/sections/${sectionId}`)
    return response.data
  },

  getRoomSections: async (roomId: number): Promise<RoomSection[]> => {
    const response = await api.get(`/sections/room/${roomId}`)
    return response.data
  },

  createSection: async (sectionData: RoomSectionCreate): Promise<RoomSection> => {
    const response = await api.post('/sections', sectionData)
    return response.data
  },

  // Create section during an active session (does not require admin)
  createSectionForSession: async (sectionData: SessionSectionCreate): Promise<RoomSection> => {
    const response = await api.post('/sections/session', sectionData)
    return response.data
  },

  updateSection: async (sectionId: number, sectionData: RoomSectionUpdate): Promise<RoomSection> => {
    const response = await api.put(`/sections/${sectionId}`, sectionData)
    return response.data
  },

  deleteSection: async (sectionId: number): Promise<void> => {
    await api.delete(`/sections/${sectionId}`)
  },

  validateSectionAnalysis: async (sectionId: number, analysisId: number): Promise<SectionValidationResult> => {
    const response = await api.get(`/sections/validate/${sectionId}/${analysisId}`)
    return response.data
  },

  // Update the section_id of an analysis result
  updateAnalysisSection: async (analysisId: number, sectionId: number | null): Promise<void> => {
    await api.patch(`/analyses/${analysisId}/section`, null, {
      params: { section_id: sectionId }
    })
  },

  // Layout import/export
  exportLayout: async (): Promise<Blob> => {
    const response = await api.get('/sections/layout/export', {
      responseType: 'blob'
    })
    return response.data
  },

  exportExampleLayout: async (): Promise<Blob> => {
    const response = await api.get('/sections/layout/example', {
      responseType: 'blob'
    })
    return response.data
  },

  validateLayoutImport: async (csvContent: string) => {
    const response = await api.post('/sections/layout/validate', null, {
      params: { csv_content: csvContent }
    })
    return response.data
  },

  importLayout: async (rows: Array<{
    room_name: string
    section_name: string
    section_description?: string
    class_name: string
    expected_quantity: number
  }>, clearExisting: boolean = false) => {
    const response = await api.post('/sections/layout/import', {
      rows,
      clear_existing: clearExisting
    })
    return response.data
  },

  // Section movement/reordering
  moveSection: async (sectionId: number, targetRoomId: number, newOrder: number = 0) => {
    const response = await api.put(`/sections/${sectionId}/move`, null, {
      params: { target_room_id: targetRoomId, new_order: newOrder }
    })
    return response.data
  },

  reorderSections: async (roomId: number, sectionOrder: number[]) => {
    const response = await api.put(`/sections/reorder/${roomId}`, null, {
      params: { section_order: sectionOrder.join(',') }
    })
    return response.data
  },

  // Testing Playground API (admin only)
  getTestingSessions: async (): Promise<{ sessions: Session[] }> => {
    const response = await api.get('/testing/sessions')
    return response.data
  },

  createTestingSession: async (data: TestingSessionCreate): Promise<Session> => {
    const response = await api.post('/testing/sessions', data)
    return response.data
  },

  getTestingSession: async (sessionId: number): Promise<Session> => {
    const response = await api.get(`/testing/sessions/${sessionId}`)
    return response.data
  },

  updateTestingSession: async (sessionId: number, data: SessionUpdate): Promise<Session> => {
    const response = await api.put(`/testing/sessions/${sessionId}`, data)
    return response.data
  },

  deleteTestingSession: async (sessionId: number): Promise<void> => {
    await api.delete(`/testing/sessions/${sessionId}`)
  },

  getTestingSessionAnalyses: async (sessionId: number): Promise<SessionAnalysesResponse> => {
    const response = await api.get(`/testing/sessions/${sessionId}/analyses`)
    return response.data
  },
}

export default api
